import React from 'react';
// @ts-ignore - Remotion型定義の互換性問題の暫定対応
import { AbsoluteFill, useCurrentFrame, interpolate, spring, useVideoConfig } from 'remotion';

export interface ShopIntroStyleProps {
  shopName: string;
  tagline: string;
  features: string[];
  ctaText: string;
  primaryColor: string;
  aspectRatio: '1:1' | '16:9' | '9:16';
}

export const ShopIntroStyle: React.FC<ShopIntroStyleProps> = ({
  shopName,
  tagline,
  features,
  ctaText,
  primaryColor,
  aspectRatio,
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const isPortrait = aspectRatio === '9:16';

  // Curtain reveal animation
  const curtainProgress = interpolate(frame, [0, 30], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // Shop name elegant fade-in
  const nameOpacity = spring({
    frame: frame - 20,
    fps,
    config: { damping: 100 },
  });
  const nameScale = interpolate(frame, [20, 50], [0.9, 1], {
    extrapolateRight: 'clamp',
  });

  // Tagline slide up
  const taglineOpacity = interpolate(frame, [50, 70], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const taglineY = interpolate(frame, [50, 70], [20, 0], {
    extrapolateRight: 'clamp',
  });

  // Decorative line expand
  const lineWidth = interpolate(frame, [70, 100], [0, 100], {
    extrapolateRight: 'clamp',
  });

  // Features stagger
  const getFeatureAnim = (index: number) => {
    const start = 100 + index * 30;
    return {
      opacity: interpolate(frame, [start, start + 20], [0, 1], {
        extrapolateRight: 'clamp',
      }),
      translateY: interpolate(frame, [start, start + 20], [30, 0], {
        extrapolateRight: 'clamp',
      }),
    };
  };

  // CTA fade in
  const ctaStart = 100 + features.length * 30 + 30;
  const ctaOpacity = interpolate(frame, [ctaStart, ctaStart + 20], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const ctaGlow = Math.sin((frame - ctaStart) / 12) * 0.3 + 0.7;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#faf9f6',
        fontFamily: "'Hiragino Mincho ProN', 'Noto Serif JP', serif",
        overflow: 'hidden',
      }}
    >
      {/* Subtle texture overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.03) 100%)`,
        }}
      />

      {/* Top decorative border */}
      <div
        style={{
          position: 'absolute',
          top: isPortrait ? '40px' : '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: `${lineWidth}%`,
          maxWidth: isPortrait ? '80%' : '60%',
          height: '1px',
          background: primaryColor,
          opacity: 0.4,
        }}
      />

      {/* Bottom decorative border */}
      <div
        style={{
          position: 'absolute',
          bottom: isPortrait ? '40px' : '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: `${lineWidth}%`,
          maxWidth: isPortrait ? '80%' : '60%',
          height: '1px',
          background: primaryColor,
          opacity: 0.4,
        }}
      />

      {/* Main content */}
      <AbsoluteFill
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          padding: isPortrait ? '80px 30px' : '60px 100px',
          opacity: curtainProgress,
        }}
      >
        {/* Shop name */}
        <div
          style={{
            opacity: nameOpacity,
            transform: `scale(${nameScale})`,
            marginBottom: '12px',
          }}
        >
          <h1
            style={{
              fontSize: isPortrait ? '44px' : '56px',
              fontWeight: 600,
              color: '#2c2c2c',
              textAlign: 'center',
              letterSpacing: '4px',
            }}
          >
            {shopName}
          </h1>
        </div>

        {/* Tagline */}
        <div
          style={{
            opacity: taglineOpacity,
            transform: `translateY(${taglineY}px)`,
            marginBottom: '40px',
          }}
        >
          <p
            style={{
              fontSize: isPortrait ? '18px' : '22px',
              color: '#666',
              textAlign: 'center',
              letterSpacing: '2px',
            }}
          >
            {tagline}
          </p>
        </div>

        {/* Decorative separator */}
        <div
          style={{
            width: `${lineWidth * 0.4}%`,
            height: '2px',
            background: primaryColor,
            marginBottom: '40px',
            opacity: 0.6,
          }}
        />

        {/* Features */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          {features.slice(0, 3).map((feature, index) => {
            const anim = getFeatureAnim(index);
            return (
              <div
                key={index}
                style={{
                  opacity: anim.opacity,
                  transform: `translateY(${anim.translateY}px)`,
                  marginBottom: '20px',
                }}
              >
                <span
                  style={{
                    fontSize: isPortrait ? '20px' : '24px',
                    color: '#444',
                    fontWeight: 400,
                    letterSpacing: '1px',
                  }}
                >
                  {feature}
                </span>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div
          style={{
            opacity: ctaOpacity,
          }}
        >
          <div
            style={{
              border: `2px solid ${primaryColor}`,
              color: primaryColor,
              padding: isPortrait ? '14px 44px' : '16px 52px',
              fontSize: isPortrait ? '18px' : '22px',
              fontWeight: 500,
              letterSpacing: '2px',
              opacity: ctaGlow,
            }}
          >
            {ctaText}
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};
